import {
  deletePaymentInstrument,
  InstrumentStatus,
  setDefaultPaymentInstrument,
} from '@foundation/apis/payments';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CheckMarkIcon,
  ConfirmationModal,
  DeleteIcon,
  Paragraph,
  SimpleButton,
  disableUIBlocker,
  enableUIBlocker,
} from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { triggerErrorToast } from '@/libs/toasts';
import { type IManagePaymentInstrument } from '@/services/payments/instruments/MandateReference';
import { revokeMandate } from '@/services/payments/instruments/PaymentInstrumentsApi';

import { isDeletable } from './helper';
import { PaymentInstrumentCard } from './PaymentInstrumentCard/PaymentInstrumentCard';

interface IEditStoredInstrumentSectionProps {
  readonly instrument: IManagePaymentInstrument;
  readonly instrumentName: string;
  readonly isVisible: boolean;
  hideSection(): void; // this should notify the parent element that current element needs to be hidden
}

const styles = {
  sectionContainer:
    'absolute w-fit overflow-hidden transition-all ease-in-out duration-300 z-10 bg-white ring-1 ring-silver-700 rounded-md p-xs right-0',
  innerContainer: 'flex flex-col gap-xs',
  button: 'min-h-[44px] py-1 px-2 flex items-center gap-2 justify-start',
  separator: 'border-b border-silver-700 w-full',
  checkMarkIcon: 'text-black w-[18px] h-[18px] self-center inline-block',
  deleteIcon: 'text-red-700 w-[18px] h-[18px] self-center inline-block',
  nfcIcon: 'text-white',
  chipIcon: 'text-white',
  customerName: 'text-white text-sm',
  modal: {
    description: 'mt-sm md:mt-md',
    buttonContainer: 'flex flex-col-reverse gap-3 mt-sm md:mt-md',
    secondaryButton: '!border-0',
  },
};

export function EditStoredInstrumentSection({
  instrument,
  instrumentName,
  hideSection,
  isVisible,
}: IEditStoredInstrumentSectionProps) {
  const { accessToken } = useAuth();
  const [removingInstrument, setRemovingInstrument] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  const isExpired = instrument.status === InstrumentStatus.Expired;
  const isMandate = instrument.mandateReference !== undefined;

  const removeStoredInstrumentComposition = (
    <>
      <PaymentInstrumentCard
        iconName={instrument.iconName}
        maskedInfo={instrument.maskedInfo}
        expired={isExpired}
      />
      <div className="flex flex-row text-md text-silver w-full justify-center gap-xs">
        <Paragraph
          ellipsisOnOverflow
          extraClassName="!w-fit"
        >
          {instrumentName}
          {instrument.expirationDate && ', '}
        </Paragraph>
        {instrument.expirationDate && (
          <span>
            <Paragraph
              ellipsisOnOverflow
              extraClassName="!w-fit"
            >
              {`${t('Exp. date')} ${instrument.expirationDate}`}
            </Paragraph>
          </span>
        )}
      </div>
    </>
  );

  return (
    <>
      <div
        className={cn(
          styles.sectionContainer,
          isVisible
            ? ' max-h-screen opacity-100 visible'
            : ' max-h-0 opacity-0 invisible pointer-events-none',
        )}
      >
        <div className={cn(styles.innerContainer)}>
          {!isMandate && !instrument.isDefault && !isExpired && (
            <>
              <SimpleButton
                className={styles.button}
                onClick={async () => {
                  try {
                    enableUIBlocker();
                    hideSection();
                    await setDefaultPaymentInstrument({
                      instrumentId: instrument.id + '',
                      bearerToken: accessToken ?? '',
                    });
                    searchParams.set('status', RedirectStatus.Success);
                    setSearchParams(searchParams);
                    window.location.reload();
                  } catch {
                    triggerErrorToast(
                      t('Something went wrong!'),
                      t('Failed to make this payment method a default one.'),
                    );
                  } finally {
                    disableUIBlocker();
                  }
                }}
              >
                <span className="flex gap-2 opacity-80  hover:opacity-100 text-black">
                  <CheckMarkIcon className={styles.checkMarkIcon} />
                  {t('Make default')}
                </span>
              </SimpleButton>
              <div className={styles.separator} />
            </>
          )}

          {isDeletable(instrument) && (
            <SimpleButton
              className={styles.button}
              onClick={() => {
                setRemovingInstrument(true);
              }}
            >
              <span className="flex gap-2 opacity-80  hover:opacity-100 text-red-700">
                <DeleteIcon className={styles.deleteIcon} />
                {isMandate ? t('Revoke mandate') : t('Delete')}
              </span>
            </SimpleButton>
          )}
        </div>
      </div>

      <ConfirmationModal
        title={
          isMandate
            ? t('Revoke this mandate?')
            : t('Remove this payment method?')
        }
        description={removeStoredInstrumentComposition}
        confirmBtnTitle={isMandate ? t('Revoke mandate') : t('Yes, Remove')}
        cancelBtnTitle={t('Go back')}
        isOpen={removingInstrument}
        styles={styles.modal}
        onClose={() => {
          setRemovingInstrument(false);
          hideSection();
        }}
        onConfirm={async () => {
          try {
            enableUIBlocker();
            setRemovingInstrument(false);
            hideSection();
            if (instrument.mandateReference !== undefined) {
              await revokeMandate(
                instrument.mandateReference,
                accessToken ?? '',
              );
            } else {
              await deletePaymentInstrument({
                instrumentId: '' + instrument.id,
                bearerToken: accessToken ?? '',
              });
            }
            searchParams.set('status', RedirectStatus.Success);
            setSearchParams(searchParams);
            window.location.reload();
          } catch {
            setRemovingInstrument(false);
            triggerErrorToast(
              t('Something went wrong!'),
              isMandate
                ? t('Failed to revoke mandate.')
                : t('Failed to delete payment method.'),
            );
          } finally {
            disableUIBlocker();
          }
        }}
      />
    </>
  );
}
